import React from 'react';
import { Formik, useFormikContext } from 'formik';

import { DEFAULT_LOCALE } from '~/components/CmsMain/localeGlobals';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { getPopoverTriggerAria } from '~/components/ui/atoms/Popover/usePopoverState';

import CardDialog from '../../CardDialog';
import { WithConfirmFormik } from '../../ConfirmModal';
import { ShowOnlyTextField, TextFieldFormik as TextFieldFormikUntyped } from '../../TextFieldFormik';

import HolidaySubdivisionSelectFormik, { NO_HOLIDAY_SUBDIVISION } from './HolidaySubdivisionSelectFormik';
import type { OnUpdateOrganizationSettings, SettingsOrganization, TextFieldFormikSelectProps } from './types';
import { useHolidaySubdivisions } from './useHolidaySubdivisions';
import { useSelectableLocales } from './useSelectableLocales';

import { useStyles } from '../../../assets/styles';

// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikSelectProps>;

const territoryFromLocale = (locale?: string): string | undefined => (locale || '').split('-')[1]?.toUpperCase();

/**
 * Both keys are non-optional strings: the select always holds a concrete value, and "no subdivision"
 * is the `NO_HOLIDAY_SUBDIVISION` empty string rather than an absent key.
 */
interface LocaleSettingFormValues {
  locale: string;
  holiday_subdiv: string;
}

interface LocaleSettingFormProps {
  organization: SettingsOrganization;
  onCancel: () => void;
}

function LocaleSettingForm({ organization, onCancel }: LocaleSettingFormProps): React.ReactElement {
  const { classes } = useStyles();
  const { values, setFieldValue, isSubmitting, handleSubmit } = useFormikContext<LocaleSettingFormValues>();
  const { territory, subdivisions, isLoading, isError } = useHolidaySubdivisions(organization.id, values.locale);
  const { locales, isLoading: isLoadingLocales, isError: isLocalesError } = useSelectableLocales(organization.id);

  // A locale the organization already carries but that this list does not offer still has to render,
  // or the select would silently show empty and Save would move the organization off it. That is the
  // only case a locale outside the offered set is put in front of the user.
  const localeOptions = locales.some(({ code }) => code === values.locale)
    ? locales
    : [{ code: values.locale, name: values.locale }, ...locales];

  // A stored subdivision belongs to the territory it was chosen under, so moving the locale to a
  // different one orphans it — and the PUT rejects the pair rather than saving a code the calendar
  // resolver would quietly ignore. Clearing it against the freshly fetched options (rather than
  // against a list of our own) is what lets the operator re-pick in the same request.
  //
  // Only ever against options that actually arrived: a failed fetch also ends with isLoading false
  // and an empty list, and treating that as staleness would wipe a perfectly valid stored code — the
  // Save below would then persist the clear.
  const isStaleSubdivision =
    !isLoading && !isError && values.holiday_subdiv && !subdivisions.some(({ code }) => code === values.holiday_subdiv);
  React.useEffect(() => {
    if (isStaleSubdivision) {
      setFieldValue('holiday_subdiv', NO_HOLIDAY_SUBDIVISION);
    }
  }, [isStaleSubdivision, setFieldValue]);

  const isSubdivisionDropped = Boolean(organization.holiday_subdiv) && !values.holiday_subdiv;

  return (
    <>
      <TextFieldFormik
        id="locale"
        label="Locale"
        className={classes.textField}
        select
        disabled={isLoadingLocales}
        // An empty list after a failed fetch means nothing about what is configurable, so it must
        // not read as "these are your options".
        helperText={isLocalesError ? 'The locales could not be loaded.' : undefined}
        fullWidth
      >
        {localeOptions.map(({ code, name }) => (
          <MenuItem key={code} value={code}>
            {`${name} (${code})`}
          </MenuItem>
        ))}
      </TextFieldFormik>

      <HolidaySubdivisionSelectFormik
        id="holiday_subdiv"
        territory={territory}
        subdivisions={subdivisions}
        isLoading={isLoading}
        isError={isError}
      />

      <div className={classes.buttonsContainer}>
        <CancelButton disabled={isSubmitting} onClick={onCancel} />
        <WithConfirmFormik
          title="Change the organization's locale?"
          primaryButtonName="Change locale"
          shouldCloseOnPrimary
          contentText={
            <>
              <Typography display="block" variant="body1">
                {`Moving this organization from ${organization.locale} to ${values.locale} also changes:`}
              </Typography>
              <ul>
                <li>region-specific behaviour across the product (US / UK / AU / EU)</li>
                <li>the US state labels offered on claims</li>
                <li>the region-aware options the rules engine builds</li>
                <li>date, number and currency formatting</li>
                <li>the holiday calendar behind every business-day deadline</li>
              </ul>
              {isSubdivisionDropped ? (
                <Typography display="block" variant="body1">
                  {`The holiday subdivision ${organization.holiday_subdiv} does not exist in ${
                    territory || territoryFromLocale(values.locale)
                  }, so it will be cleared and the country-level calendar will apply.`}
                </Typography>
              ) : null}
            </>
          }
        >
          <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
            Save
          </Button>
        </WithConfirmFormik>
      </div>
    </>
  );
}

interface LocaleSettingProps {
  organization: SettingsOrganization;
  onUpdateSettings: OnUpdateOrganizationSettings;
}

function LocaleSetting({ organization, onUpdateSettings }: LocaleSettingProps): React.ReactElement {
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLElement | undefined>(undefined);
  const popoverId = React.useId();
  const { classes } = useStyles();

  return (
    <>
      <ShowOnlyTextField
        classes={classes}
        label="Locale"
        showOnlyValueComponent={organization.locale || '—'}
        onEdit={(e: React.MouseEvent<HTMLElement>) => setPopoverAnchorEl(e.currentTarget)}
        editTriggerAria={getPopoverTriggerAria(Boolean(popoverAnchorEl), popoverId)}
      />

      <CardDialog
        isPopover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        id={popoverId}
        title="Locale"
        onClose={() => setPopoverAnchorEl(undefined)}
      >
        <Formik<LocaleSettingFormValues>
          initialValues={{
            locale: organization.locale || DEFAULT_LOCALE,
            holiday_subdiv: organization.holiday_subdiv || NO_HOLIDAY_SUBDIVISION,
          }}
          enableReinitialize
          onSubmit={async (values, formikProps) => {
            try {
              // Both keys go in one request: the server validates the pair, so submitting the
              // locale alone would be rejected whenever the move orphans a stored subdivision.
              await onUpdateSettings({
                locale: values.locale,
                holiday_subdiv: values.holiday_subdiv || null,
              });
              setPopoverAnchorEl(undefined);
            } catch {
              formikProps.setSubmitting(false);
            }
          }}
        >
          <LocaleSettingForm
            organization={organization}
            onCancel={() => {
              setPopoverAnchorEl(undefined);
            }}
          />
        </Formik>
      </CardDialog>
    </>
  );
}

export default LocaleSetting;
